<script lang="ts">
// this must be exported here for the keep alive on reroute
export default {
  name: "Dealers",
};
</script>

<script setup lang="ts">
import ServiceService from "@/api/service";
import DealersTable from "@/components/models/dealer/DealersTable.vue";
import { useSnackStore } from "@/stores/snack";
import { useQuery } from "@tanstack/vue-query";
import { definePage } from "unplugin-vue-router/runtime";
import { onMounted, provide, ref } from "vue";

import DealerService, { type TDealer } from "@/api/dealer";
import { ActionsEnum, SubjectsEnum, type AppAbility } from "@/services/ability";
import type { PaginatedType, VFormFieldChoice } from "@/utils/DjangoTypes";
import { useAbility } from "@casl/vue";
import { DefaultServerTableParams, type ServerTableParamsType } from "@/utils/TableUtils";

definePage({
  meta: {
    title: "Dealers",
    checkPrivileges: () => {
      const { can } = useAbility<AppAbility>();
      return can(ActionsEnum.USE, SubjectsEnum.DEALER);
    },
  },
});

const snackStore = useSnackStore();
const dealersFetchEnabled = ref(false);

const serviceChoices = ref<VFormFieldChoice<any>[]>([]);

const queriesTableParams: Record<string, ServerTableParamsType> = {
  dealers: DefaultServerTableParams,
};
provide("queriesTableParams", queriesTableParams);

const {
  data: dealers,
  isPending: isPending,
  isFetching: isFetching,
} = useQuery({
  queryKey: ["dealers"],
  queryFn: async (): Promise<PaginatedType<TDealer[]> | undefined> => {
    try {
      const [dealers, serviceTypesSelectable] = await Promise.all([
        DealerService.dealer.getAll(queriesTableParams.dealers),
        ServiceService.service.getAllSelectable({ ...DefaultServerTableParams, itemsPerPage: 100 }),
      ]);

      serviceChoices.value = serviceTypesSelectable;

      return dealers.data;
    } catch (error) {
      snackStore.showError(error);
    }
  },
  enabled: () => dealersFetchEnabled.value,
});

onMounted(async () => {
  dealersFetchEnabled.value = true;
});
</script>

<template>
  <v-container fluid>
    <div class="tw:!mb-10">
      <dealers-table
        :query-key="'dealers'"
        :data="dealers"
        :services="serviceChoices"
        :loading="isPending || isFetching"
      />
    </div>
  </v-container>
</template>

<style scoped></style>
